import React from "react";
import { Divider, Typography, Button } from "@mui/material";
import { UserCircle, ArrowUpRight } from "@phosphor-icons/react";
import { useHistory } from "react-router-dom";
import { useGetTeams } from "hooks/api/useGetTeams";
import { useGetTeamMembers } from "hooks/api/useGetTeamMembers";

interface CaregiverTeamSelectionProps {
  userId: number;
  isOpen: boolean;
  onClose?: () => void;
  selectedReassignments: Map<number, number>; // Map<teamId, newCaregiverId>
  onSelectionChange: (teamId: number, newCaregiverId: number | null) => void;
}

/**
 * CaregiverTeamSelection component
 * Displays teams where a user is a team member and allows reassigning the designated caregiver role.
 * Shows all teams where the user is a member (not just where they're the designated caregiver)
 * to allow reassigning multiple teams if needed.
 */
const CaregiverTeamSelection: React.FC<CaregiverTeamSelectionProps> = ({
  userId,
  isOpen,
  onClose,
  selectedReassignments,
  onSelectionChange,
}) => {
  const history = useHistory();

  // Get teams where this user is a team member (not just where they're the designated caregiver)
  // This ensures we can still see teams after reassignment
  const { teams, teamsLoading } = useGetTeams(
    isOpen && userId ? { userId: userId, includeClientDetails: true } : null
  );

  // Get team members for each team
  const teamIds = teams?.map((team: any) => team.id) || [];
  const { teamMembers, teamMembersLoading } = useGetTeamMembers(
    isOpen && teamIds.length > 0 ? { teamIds } : null
  );

  // Filter teams to only show ones where the user is still a member
  // This handles the case where the user might have been removed from some teams
  const teamsWhereUserIsMember = React.useMemo(() => {
    if (!teams || !teamMembers) return [];

    return teams.filter((team: any) => {
      const members = teamMembers.filter(
        (member: any) => member.teamId === team.id && member.hasAcceptedInvite
      );
      return members.some((member: any) => member.userId === userId);
    });
  }, [teams, teamMembers, userId]);

  if (!isOpen) return null;

  if (teamsLoading || teamMembersLoading) {
    return <div>Loading team information...</div>;
  }

  if (!teamsWhereUserIsMember || teamsWhereUserIsMember.length === 0) {
    return (
      <div style={{ marginBottom: "20px" }}>
        <Typography variant="body2" color="textSecondary">
          No teams found for this user.
        </Typography>
      </div>
    );
  }

  // Group team members by team
  const teamMembersByTeam = teamsWhereUserIsMember.reduce(
    (acc: Record<number, any>, team: any) => {
      // Only include team members who have accepted their invitations (exclude pending/expired invites)
      const members =
        teamMembers?.filter(
          (member: any) => member.teamId === team.id && member.hasAcceptedInvite
        ) || [];

      // Get client information from the team (teams should have clientId and client details if includeClientDetails is true)
      const teamClient =
        team.client || (team.clientId ? { id: team.clientId, name: team.clientName } : null);

      acc[team.id] = {
        team,
        members,
        client: teamClient,
      };
      return acc;
    },
    {} as Record<number, any>
  );

  const handleSelectCaregiver = (teamId: number, newCaregiverId: number) => {
    // Toggle selection: if already selected, deselect; otherwise, select
    const currentSelection = selectedReassignments.get(teamId);
    if (currentSelection === newCaregiverId) {
      // Deselect if clicking the same caregiver
      onSelectionChange(teamId, null);
    } else {
      // Select new caregiver
      onSelectionChange(teamId, newCaregiverId);
    }
  };

  const handleAddTeamMember = (clientId: number) => {
    const currentPath = window.location.pathname;
    const profilePath = `/clients/${clientId}/profile`;

    // If already on the profile page, just close the dialog
    if (currentPath === profilePath) {
      if (onClose) {
        onClose();
      }
    } else {
      // Navigate to the profile page
      history.push(profilePath);
    }
  };

  return (
    <div>
      {Object.values(teamMembersByTeam).map(({ team, members, client }: any, index: number) => {
        // Determine the current designated caregiver:
        // If a selection has been made, use that; otherwise use the one from the API
        const selectedCaregiverId = selectedReassignments.get(team.id);
        const apiDesignatedCaregiver = members.find((member: any) => member.documentSigner);
        const currentDesignatedCaregiverId = selectedCaregiverId || apiDesignatedCaregiver?.userId;
        const currentDesignatedCaregiver = members.find(
          (member: any) => member.userId === currentDesignatedCaregiverId
        );

        const isLastTeam = index === Object.values(teamMembersByTeam).length - 1;

        return (
          <div key={team.id}>
            <div style={{ marginBottom: "24px" }}>
              {/* Team header with icon and name */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "16px",
                  gap: "8px",
                }}
              >
                <UserCircle size={20} weight="duotone" />

                <Typography variant="h6" style={{ fontSize: "16px", margin: 0 }}>
                  {client?.name ? `${client.name}'s Team` : team.name || "Team"}
                </Typography>
              </div>

              {/* All team members */}
              {members.length > 0 ? (
                members.map((member: any) => {
                  const isCurrentDesignatedCaregiver =
                    member.userId === currentDesignatedCaregiverId;
                  return (
                    <div
                      key={member.userId}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 0",
                      }}
                    >
                      <div>
                        <Typography variant="body1">{member.name}</Typography>
                      </div>
                      <div>
                        {isCurrentDesignatedCaregiver ? (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{
                              fontStyle: "italic",
                              fontSize: "14px",
                            }}
                          >
                            Designated Caregiver
                          </Typography>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            onClick={() => handleSelectCaregiver(team.id, member.userId)}
                          >
                            Set As Designated Caregiver
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 0",
                  }}
                >
                  <div>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{
                        fontStyle: "italic",
                      }}
                    >
                      No team members found
                    </Typography>
                  </div>
                  <div>
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      endIcon={<ArrowUpRight size={16} />}
                      onClick={() => {
                        if (client?.id) {
                          handleAddTeamMember(client.id);
                        }
                      }}
                    >
                      Add Team Member
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Add divider between teams, but not after the last team */}
            {!isLastTeam && <Divider style={{ margin: "16px 0" }} />}
          </div>
        );
      })}
    </div>
  );
};

export default CaregiverTeamSelection;
